import { GuidePriority } from "../guide";
import type { EndFacts, GuideContext, GuideStep, HubFacts, LevelFacts } from "../guide";
import { ResearchTargets, researchHubSteps } from "./research";
import { CardsTargets, cardsHubSteps } from "./cards";
import { passHubSteps } from "./season";
import { eventModeLevelSteps } from "./eventModes";
import { suppliesHubSteps } from "./supplies";
import { trophiesHubSteps } from "./trophies";
import { petsHubSteps, petsLevelSteps } from "./pets";

/**
 * Captain Bram on everything waves 1–3 added (FINISH_PLAN WP9 O6): just-in-time lines, each once per save, at the
 * moment the thing is on screen: UNDO in the draft, the enemy card and elites, the whole map, Heroic and Trial runs,
 * the checkpoint retry, the Shop and its crates, Tribute, the Market, the upgrade queue, events, achievements, skins
 * and the comfort settings. They join the existing scripts (levelHints, hub, end), so the queue's rules hold: one at a
 * time, never over a banner, a sheet or a dialog.
 *
 * Every one names its target and `needsTarget`: a line about a button that is not drawn (a feature not landed, a
 * builder free so no QUEUE NEXT) is simply not wanted, so it never points at nothing and never holds up the check-in
 * card. The hub's and the battle's feature tips are `onePerVisit`: a returning player meets them one per visit.
 */

/** Selectors of the targets. The `data-guide` ones are stable ids features add for the guide (WP2's UNDO has one). */
export const FeatureTargets = {
  undo: "#hand [data-guide=undo]",
  wholeMap: "#rotate-map .whole",
  heroicTag: "#hud .runtag.heroic",
  trialTag: "#hud .runtag.trial",
  /** M21 (docs/MODES.md): the Iron and Endless run tags, the battle card's IRON button. */
  ironTag: "#hud .runtag.iron",
  endlessTag: "#hud .runtag.endless",
  ironButton: ".hub .battlecard .ironbtn",
  checkpoint: ".end [data-c=checkpoint]",
  /** The victory's star row with every condition met (a browser without :has() never matches: no line). */
  allStars: ".end .starconds:not(:has(.cond:not(.on)))",
  /** Shop redesign (2026-10-19): the top bar shows gems only, as the Store button; gold shows on the Towers tab, Marks
   * on the Shop tabs that spend them, so their tips point at those tabs. */
  gemsPill: ".hub .fpill[data-pill=gems] .storebtn",
  goldPill: ".hub .tabbar .tab[data-tab=towers]",
  marksPill: ".hub .tabbar .tab[data-tab=shop]",
  tribute: ".hub .tchip:not(.hidden)",
  eventChip: ".hub .evhubchip:not([hidden])",
  /** M35: the Events tab (events, Trials, Heroic, Endless, Free rewards) and the top bar's ⚙ menu (Achievements, Settings). */
  eventsTab: ".tab[data-tab=events]",
  menuButton: ".hub .topbar .menubtn",
  /** Supply Crates retired (2026-10-21): the Shop tip points at the Supplies tab's first panel (speed-ups). */
  crates: ".sheet.shop .sgoods .ssec.packs",
  /** M35: the Shop tab (Research, the currency pages). */
  shopTab: ".tab[data-tab=shop]",
  queueNext: ".sheet.upgrade .queuego",
  /**
   * WP8's skin picker entries: the Towers sheet head's Skins button and the Fortress card's brush (towersTab.ts), or
   * any entry a later change marks `data-guide="skins"`.
   */
  skins: "[data-guide=skins], .sheet .skinsbtn, .sheet .fbrush",
  /**
   * Gear v2 in the redesigned Armoury (2026-10-21): the Loadout's "Get stronger" cards (one item level for essence, in
   * one tap; the strip shows them once the stash has nothing better for the owner, or from the dock's meter) — the
   * header's essence counter opens the Forge (fusion), not a raise — and the header's FUSE chip; the hub's Gear tab.
   */
  getStronger: ".sheet.armoury .arm-for.m-stronger .upc[data-id]:not(.short)",
  fuseChip: ".sheet.armoury .arm-head .fusechip",
  gearTab: ".tab[data-tab=gear]",
} as const;

/**
 * Tips that point into a hub sheet: while one is not yet done, a sheet holding its target is not "busy" for the guide
 * (ui/hub/hubGuide.ts holdsGuideTarget), so the tip can speak over its own sheet.
 */
export const HUB_SHEET_TIPS: readonly { readonly step: string; readonly target: string }[] = [
  { step: "hub.crates", target: FeatureTargets.crates },
  { step: "hub.queue", target: FeatureTargets.queueNext },
  { step: "hub.skins", target: FeatureTargets.skins },
  { step: "hub.forgeRaise", target: FeatureTargets.getStronger },
  { step: "hub.forgeFuse", target: FeatureTargets.fuseChip },
  { step: "hub.researchRow", target: ResearchTargets.researchRow },
  { step: "hub.cards", target: CardsTargets.cardsPage },
];

/** In-battle extras start on level 2 (level 1 has its walkthrough); the enemy card and the whole map a level later. */
const ExtrasFromLevel = 2;
const ScoutingFromLevel = 3;
/** The enemy-card tip waits this long into a wave, so raiders are on the road to tap. */
const EnemyInfoAfterSeconds = 6;
/** Hub feature tips wait for the first session to be over (hub.ts HINTS_FROM_CLEARED). */
const HubTipsFromCleared = 2;
/** The comfort settings tip comes a couple of sessions in, once the basics are known. */
const SettingsTipFromCleared = 4;
/** The Heroic line on a three-star victory: not on the first wins (the first chest has the stage then). */
const HeroicEndFromCleared = 3;

const L = (c: GuideContext): LevelFacts | null => (c.screen === "level" ? c.level : null);
const hub = (c: GuideContext): HubFacts | null => (c.screen === "hub" ? c.hub : null);
/** On the lobby itself (no sheet) with the first session over. */
const lobby = (c: GuideContext): HubFacts | null => { const h = hub(c); return h && h.sheet === null && h.highestCleared >= HubTipsFromCleared ? h : null; };
/**
 * In a sheet with the first session over: during it the captain is walking the player through Equip best and the first
 * upgrade, and a tip about QUEUE NEXT (the one builder just got busy) or Skins would cut into that lesson.
 */
const sheetAfterFirstSession = (c: GuideContext): HubFacts | null => { const h = hub(c); return h && h.highestCleared >= HubTipsFromCleared ? h : null; };
const end = (c: GuideContext): EndFacts | null => (c.screen === "end" ? c.end : null);
const drafting = (l: LevelFacts | null) => l !== null && (l.phase === "draft" || l.phase === "ready");
const moreRow = (h: HubFacts, id: string) => h.extras.moreRows.includes(id);

/** A tip with its target required: the shape almost every line here takes. */
const TIP = { kind: "tip", block: "none", needsTarget: true } as const;

const levelSteps: GuideStep[] = [
  { ...TIP, id: "hint.undo", screen: "level", pose: "pointing", topic: "undo", onePerVisit: true,
    target: { dom: FeatureTargets.undo },
    text: "Changed your mind? **UNDO** takes back your last card, right up until the waves start.",
    when: (c) => { const l = L(c); return drafting(l) && l!.level >= ExtrasFromLevel; } },
  { ...TIP, id: "hint.enemyInfo", screen: "level", pose: "pointing", topic: "scouting", onePerVisit: true,
    target: { world: "leadEnemy" },
    text: "Tap any raider to see what it is, and which towers it's **weak to**.",
    when: (c) => { const l = L(c); return l !== null && l.level >= ScoutingFromLevel && l.phase === "wave" && l.waveSeconds >= EnemyInfoAfterSeconds; } },
  { id: "hint.elite", kind: "info", screen: "level", pose: "pointing", holdSim: true, priority: GuidePriority.NewThing, topic: "scouting",
    target: { world: "elite", focus: true }, needsTarget: true,
    text: "Watch out: an **elite**, with a nasty twist. Tap it to see what beats it.",
    when: (c) => L(c)?.eliteAlive === true,
    until: (c) => L(c)?.eliteAlive !== true },
  { ...TIP, id: "hint.wholeMap", screen: "level", pose: "neutral", topic: "map", onePerVisit: true,
    target: { dom: FeatureTargets.wholeMap },
    text: "Lost the road? Tap the **map** button or double-tap the ground to see the whole field.",
    when: (c) => { const l = L(c); return l !== null && l.level >= ScoutingFromLevel && l.phase === "wave"; } },
  { ...TIP, id: "hint.heroic", screen: "level", pose: "pointing", priority: GuidePriority.NewThing, topic: "events",
    target: { dom: FeatureTargets.heroicTag },
    text: "A **Heroic** battle! Tougher raiders, but a win earns a Heroic star and **Marks**.",
    when: (c) => L(c) !== null },
  { ...TIP, id: "hint.trial", screen: "level", pose: "pointing", priority: GuidePriority.NewThing, topic: "events",
    target: { dom: FeatureTargets.trialTag },
    // The hub (hub.trial) and its unlock card have said what a Trial is; in the run, point at its twist.
    text: "This tag shows the **Trial**'s twist. Win the battle for this week's reward.",
    when: (c) => L(c) !== null },
  { ...TIP, id: "hint.iron", screen: "level", pose: "pointing", priority: GuidePriority.NewThing, topic: "iron",
    target: { dom: FeatureTargets.ironTag },
    text: "**Iron**: one long wave and just your three towers. Spend the road wisely, Commander!",
    when: (c) => L(c) !== null },
  { ...TIP, id: "hint.endless", screen: "level", pose: "laughing", topic: "endless",
    target: { dom: FeatureTargets.endlessTag },
    text: "**Endless**: each wave hits harder than the last. Hold as long as you can: waves are your score.",
    when: (c) => L(c) !== null },
];

/** The forge's facts (gear v2), when the features have registered them. */
const forge = (h: HubFacts | null) => h?.extras.forge ?? null;

const hubSteps: GuideStep[] = [
  // M22 gear v2: the first essence that can raise a worn item, the first fusion the stash could make, the first free
  // reward waiting. In the Armoury the line points at its own counter / FUSE badge; on the lobby at the way in.
  { ...TIP, id: "hub.forgeEssence", screen: "hub", pose: "pointing", priority: GuidePriority.NewThing, topic: "forge",
    target: { dom: FeatureTargets.gearTab }, unless: ["hub.forgeRaise"],
    text: "You've earned **Forge Essence**: it raises your gear's level. Open the **Armoury** to spend it!",
    when: (c) => { const h = lobby(c); return h !== null && forge(h)?.raisable === true; } },
  { ...TIP, id: "hub.forgeRaise", screen: "hub", pose: "pointing", priority: GuidePriority.NewThing, topic: "forge",
    target: { dom: FeatureTargets.getStronger },
    text: "**Get stronger** spends **Forge Essence**: tap a card to raise that item a level. Every level adds stats.",
    when: (c) => { const h = sheetAfterFirstSession(c); return h !== null && h.sheet === "gear" && forge(h)?.raisable === true; } },
  { ...TIP, id: "hub.forgeFuse", screen: "hub", pose: "laughing", priority: GuidePriority.NewThing, topic: "forge",
    target: { dom: FeatureTargets.fuseChip },
    text: "Ha, three of a kind! Three items of one rarity **fuse** into one of the next: tap **FUSE**.",
    when: (c) => { const h = sheetAfterFirstSession(c); return h !== null && h.sheet === "gear" && (forge(h)?.fusable ?? 0) > 0; } },
  { ...TIP, id: "hub.freeRewards", screen: "hub", pose: "thumbsUp", topic: "forge", onePerVisit: true,
    target: { dom: FeatureTargets.eventsTab },
    text: "A **free reward** is waiting: essence, sigils or a chest! Claim it in **Events → Free rewards**.",
    when: (c) => { const h = lobby(c); return h !== null && (forge(h)?.freeRewards ?? 0) > 0 && moreRow(h, "freerewards"); } },
  // M30 #9 (Kevin: "explain the currencies"): the first time each coin matters, a line on its pill — a tap there opens
  // its card (ui/hub/currencyCard.ts: what it is, how it is earned, what it buys).
  { ...TIP, id: "hub.gold", screen: "hub", pose: "pointing", topic: "currencies", onePerVisit: true,
    target: { dom: FeatureTargets.goldPill },
    text: "**Gold** pays for tower upgrades and research. Spend it here in **Towers**!",
    when: (c) => lobby(c) !== null },
  { ...TIP, id: "hub.shop", screen: "hub", pose: "pointing", topic: "shop", onePerVisit: true,
    target: { dom: FeatureTargets.gemsPill },
    text: "These are **gems**, our rarest coin. Tap them to open the **Store**!",
    when: (c) => lobby(c) !== null },
  { ...TIP, id: "hub.crates", screen: "hub", pose: "laughing", topic: "shop",
    target: { dom: FeatureTargets.crates },
    text: "**Speed-ups** finish an upgrade sooner. And there's a **free gold pack** for you every day under **Supplies**!",
    when: (c) => { const h = sheetAfterFirstSession(c); return h !== null && h.sheet === "shop"; } },
  { ...TIP, id: "hub.tribute", screen: "hub", pose: "pointing", topic: "tribute", onePerVisit: true,
    target: { dom: FeatureTargets.tribute },
    // Its unlock card has just said the fortress gathers while you're away: this line says what to do with it.
    text: "Tap the **Tribute** chip to claim what the fortress gathered, before it fills up!",
    when: (c) => lobby(c)?.extras.tributeUnlocked === true },
  { ...TIP, id: "hub.market", screen: "hub", pose: "pointing", topic: "market", onePerVisit: true,
    target: { dom: FeatureTargets.marksPill },
    text: "Spare **Rare** gear sells for **Marks**. Spend them at the **Merchant** in the Shop!",
    when: (c) => { const h = lobby(c); return h !== null && (h.extras.marketSellable > 0 || (h.extras.marks ?? 0) > 0); } },
  { ...TIP, id: "hub.queue", screen: "hub", pose: "pointing", topic: "queue",
    target: { dom: FeatureTargets.queueNext },
    text: "Builders all busy? **QUEUE** lines this upgrade up to start the moment one is free.",
    when: (c) => { const h = sheetAfterFirstSession(c); return h !== null && h.sheet === "upgrade"; } },
  { id: "hub.events", kind: "info", screen: "hub", pose: "cheering", priority: GuidePriority.NewThing, topic: "events", onePerVisit: true,
    target: { dom: FeatureTargets.eventChip }, needsTarget: true,
    text: (c) => {
      const e = hub(c)?.extras.event;
      return e ? `**${e.name}** has begun! ${e.how ?? "Win battles and finish Orders"} for **${e.currency}**, then claim rewards here.`
        : "An **event** has begun! Win battles and finish Orders, then claim rewards here.";
    },
    when: (c) => lobby(c)?.extras.event != null },
  { ...TIP, id: "hub.trial", screen: "hub", pose: "pointing", topic: "events", onePerVisit: true,
    target: { dom: FeatureTargets.eventsTab },
    text: "A **Weekly Trial** is open: a level you've beaten, with a twist. Find it in **Events**.",
    when: (c) => { const h = lobby(c); return h !== null && h.extras.trialOpen && moreRow(h, "events"); } },
  { ...TIP, id: "hub.heroic", screen: "hub", pose: "pointing", topic: "events", onePerVisit: true,
    target: { dom: FeatureTargets.eventsTab },
    // After the victory's own line (end.heroicOpen) this is only where to find it.
    text: "Your **Heroic** tier is open! Find it in **Events** for a Heroic star and **Marks**.",
    when: (c) => { const h = lobby(c); return h !== null && h.extras.heroicOpen && moreRow(h, "events"); } },
  { id: "hub.iron", kind: "info", screen: "hub", pose: "cheering", priority: GuidePriority.NewThing, topic: "iron", onePerVisit: true,
    target: { dom: FeatureTargets.ironButton }, needsTarget: true,
    text: "Three stars open that level's **Iron** challenge: one long wave, three towers, one try a day. Win an **Iron star**!",
    when: (c) => lobby(c)?.extras.ironOpen === true },
  { ...TIP, id: "hub.endless", screen: "hub", pose: "pointing", topic: "endless", onePerVisit: true,
    target: { dom: FeatureTargets.eventsTab },
    text: "**Endless** is open: waves that never stop, and your weekly best earns a chest. Find it in **Events → Endless**.",
    when: (c) => { const h = lobby(c); return h !== null && h.extras.endlessOpen && moreRow(h, "endless"); } },
  { ...TIP, id: "hub.daily", screen: "hub", pose: "cheering", topic: "daily", onePerVisit: true,
    target: { dom: FeatureTargets.eventsTab },
    text: "The **Daily Road** is open: one road a day, the same for every Commander. Win it in **Events** for essence and **Marks**.",
    when: (c) => { const h = lobby(c); return h !== null && h.extras.dailyOpen === true && moreRow(h, "daily"); } },
  { ...TIP, id: "hub.challenge", screen: "hub", pose: "laughing", topic: "challenge", onePerVisit: true,
    target: { dom: FeatureTargets.eventsTab },
    text: "Fancy a duel? **Challenge codes** in **Events** turn a battle into a code a friend can play too.",
    when: (c) => { const h = lobby(c); return h !== null && h.extras.challengeOpen === true && moreRow(h, "challenges"); } },
  { ...TIP, id: "hub.records", screen: "hub", pose: "thumbsUp", topic: "records", onePerVisit: true,
    target: { dom: FeatureTargets.menuButton },
    text: "Your first record, Commander! The **Hall of Records** keeps your bests: find it in the **⚙ menu**.",
    when: (c) => { const h = lobby(c); return h !== null && h.extras.recordsAny === true && moreRow(h, "records"); } },
  { ...TIP, id: "hub.endlessChest", screen: "hub", pose: "thumbsUp", topic: "endless", onePerVisit: true,
    target: { dom: FeatureTargets.eventsTab },
    text: "Last week's **Endless** chest is ready. Claim it in **Events → Endless**!",
    when: (c) => lobby(c)?.extras.endlessChest === true },
  { ...TIP, id: "hub.achievements", screen: "hub", pose: "thumbsUp", topic: "achievements", onePerVisit: true,
    target: { dom: FeatureTargets.menuButton },
    text: "Well done, Commander: you've earned an **achievement**! Claim its reward in the **⚙ menu → Achievements**.",
    when: (c) => (lobby(c)?.extras.moreDots.achievements ?? 0) > 0 },
  { ...TIP, id: "hub.skins", screen: "hub", pose: "laughing", topic: "skins",
    target: { dom: FeatureTargets.skins },
    text: "Ha, fancy a new look? **Skins** dress up a tower: looks only, pure glory.",
    when: (c) => { const h = sheetAfterFirstSession(c); return h !== null && h.sheet !== null; } },
  { ...TIP, id: "hub.settings", screen: "hub", pose: "neutral", topic: "settings", onePerVisit: true,
    target: { dom: FeatureTargets.menuButton },
    text: "Playing one-handed, or want bigger words? **⚙ → Settings** has left-handed controls and **Larger text**.",
    when: (c) => (lobby(c)?.highestCleared ?? 0) >= SettingsTipFromCleared },
];

const endSteps: GuideStep[] = [
  { id: "end.checkpoint", kind: "info", screen: "end", pose: "pointing", priority: GuidePriority.NewThing, topic: "checkpoint",
    target: { dom: FeatureTargets.checkpoint }, needsTarget: true,
    text: "Not beaten yet! **RETRY FROM WAVE** picks up at the last checkpoint, free once per battle.",
    when: (c) => end(c)?.won === false },
  { ...TIP, id: "end.heroicOpen", screen: "end", pose: "laughing", topic: "events",
    target: { dom: FeatureTargets.allStars },
    text: "Ha, three stars! That opens this level's **Heroic** tier: tougher raiders, bigger rewards.",
    when: (c) => end(c)?.won === true && c.highestCleared >= HeroicEndFromCleared },
];

/** The lines each existing script gains (appended: the older lines keep their order and ties). */
export const featureLevelSteps: readonly GuideStep[] = [...levelSteps, ...eventModeLevelSteps, ...petsLevelSteps];   // MP-21: the event modes' run tags (scripts/eventModes.ts)
export const featureHubSteps: readonly GuideStep[] = [...hubSteps, ...researchHubSteps, ...cardsHubSteps, ...passHubSteps, ...suppliesHubSteps, ...trophiesHubSteps, ...petsHubSteps];   // M35: Gold Research (scripts/research.ts); MP-01 cards; MP-22: the pass (scripts/season.ts); MP-06 supplies; MP-23 Trophies; pets (docs/PETS.md)
export const featureEndSteps: readonly GuideStep[] = endSteps;

/** Every step id here, by the topic it teaches (topics.ts adds them to the feature topics' `steps`). */
export function featureStepsByTopic(): ReadonlyMap<string, readonly string[]> {
  const out = new Map<string, string[]>();
  for (const s of [...levelSteps, ...petsLevelSteps, ...featureHubSteps, ...endSteps]) if (s.topic) out.set(s.topic, [...(out.get(s.topic) ?? []), s.id]);
  return out;
}
